/**
 * MobileShell — layout pour `< 768px` : bottom tab bar + 1 panel central.
 *
 * @remarks
 * Navigation principale via une barre d'onglets basse (au pouce) ; accès compte
 * (profil, paramètres, déconnexion) via l'avatar du header ; retour contextuel
 * (flèche) piloté par le fil d'Ariane.
 *
 * Panneaux latéraux : un seul à la fois, ouvert par-dessus le contenu sur toute
 * la largeur, via les boutons panneau du header (comme sur desktop), Ctrl+B /
 * Ctrl+Shift+B ou un bouton « révéler » d'une vue (commande
 * `shell:toggle-panel`). L'état est local, pas celui du layout store : sur
 * desktop les deux panneaux sont ouverts par défaut, ils recouvriraient le
 * contenu dès l'arrivée. Changer de page referme le panneau. La vue centrale
 * reste montée dessous (une évaluation en cours ne perd pas son état).
 *
 * Les deux barres (en-tête, onglets) sont sur `bg-bg-muted` : sur fond blanc,
 * `bg-surface` ne les détachait pas du contenu.
 */

import { Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PanelLeft, PanelRight } from 'lucide-react';
import { useBreadcrumbStore, AltLogo } from '@/shared/components';
import { IconButton } from '@/shared/ui';
import { useCommand } from '@/app/commands/command-bus';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { Panel } from '@/app/panels';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { useFeatureFlagsStore } from '@/app/stores/use-feature-flags-store';
import { useCurrentContext } from '@/app/router/use-current-context';
import { useContextLifecycle } from '@/app/router/use-context-lifecycle';
import { useFeatureStoresSubscription } from '@/app/router/use-feature-stores-subscription';
import { useFeatureShortcuts } from '@/app/router/use-feature-shortcuts';
import { useFocusOnRouteChange } from '@/app/router/use-focus-on-route-change';
import { useDocumentTitle } from '@/app/router/use-document-title';
import { MobileTabBar } from './MobileTabBar';
import { SkipLink } from './SkipLink';
import { ActiveGlobalLoader } from './global-loader-variants';
import { GlobalLoaderZone } from './GlobalLoaderZone';
import { MobileAccountMenu } from './MobileAccountMenu';
import { featureRegistry, type AppContext, type Context } from '@/app/registry';

type Side = 'left' | 'right';

const PANEL_LABEL: Record<Side, string> = {
  left: 'Afficher ou masquer le panneau gauche',
  right: 'Afficher ou masquer le panneau droit',
};

export function MobileShell() {
  const context = useCurrentContext();
  const user = useAuthStore((s) => s.user);
  const flags = useFeatureFlagsStore((s) => s.flags);
  const appCtx = useMemo<AppContext>(() => ({ user, flags }), [user, flags]);

  useFeatureStoresSubscription();
  useFeatureShortcuts(context, appCtx);
  useContextLifecycle(context, appCtx);
  useFocusOnRouteChange(context);
  useDocumentTitle(context, appCtx);

  // Navigation contextuelle mobile : le fil d'Ariane publié par la vue courante
  // donne le titre (dernier item) et la cible « retour » (plus proche ancêtre
  // navigable). Sans fil (page racine), pas de flèche.
  const navigate = useNavigate();
  const breadcrumbItems = useBreadcrumbStore((s) => s.items);
  const backTo = breadcrumbItems
    .slice(0, -1)
    .filter((it) => it.to)
    .at(-1)?.to;
  const title = breadcrumbItems.at(-1)?.label;

  const CenterView = featureRegistry.getCenterView(context, appCtx);
  const leftTabs = featureRegistry.getLeftPanelTabs(context, appCtx);
  const rightTabs = featureRegistry.getRightPanelTabs(context, appCtx);
  const leftActiveTab = useLayoutStore((s) => s.leftActiveTab);
  const rightActiveTab = useLayoutStore((s) => s.rightActiveTab);
  const setLeftActiveTab = useLayoutStore((s) => s.setLeftActiveTab);
  const setRightActiveTab = useLayoutStore((s) => s.setRightActiveTab);

  // Le panneau ouvert est rattaché à la page où on l'a ouvert : changer de page
  // le referme sans effet à synchroniser.
  const [sheet, setSheet] = useState<{ side: Side; context: Context } | null>(null);
  const openSide = sheet?.context === context ? sheet.side : null;
  const sheetTabs = openSide === 'left' ? leftTabs : openSide === 'right' ? rightTabs : [];
  const toggleSheet = useCallback(
    (side: Side) => {
      setSheet((current) =>
        current?.side === side && current.context === context ? null : { side, context },
      );
    },
    [context],
  );
  useCommand('shell:toggle-panel', toggleSheet);

  const panelButton = (side: Side) => (
    <IconButton
      aria-label={PANEL_LABEL[side]}
      title={PANEL_LABEL[side]}
      aria-pressed={openSide === side}
      size="sm"
      onClick={() => {
        toggleSheet(side);
      }}
      className={
        openSide === side ? 'text-primary bg-(--color-accent-bg)' : 'text-fg-muted hover:text-fg'
      }
    >
      {side === 'left' ? (
        <PanelLeft className="size-4" aria-hidden />
      ) : (
        <PanelRight className="size-4" aria-hidden />
      )}
    </IconButton>
  );

  return (
    <div className="bg-bg text-fg flex h-screen w-screen flex-col overflow-hidden">
      <SkipLink />
      <header className="border-border bg-bg-muted flex h-12 shrink-0 items-center gap-2 border-b px-3">
        {backTo ? (
          <button
            type="button"
            onClick={() => {
              void navigate(backTo);
            }}
            aria-label="Retour"
            className="hover:bg-bg-active -ml-1 rounded-sm p-1.5"
          >
            <ArrowLeft className="size-5" />
          </button>
        ) : null}
        {/* Panneau gauche juste à droite du logo (ou du titre), comme sur desktop. */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {title ? (
            <span className="min-w-0 truncate text-sm font-medium">{title}</span>
          ) : (
            <AltLogo variant="symbol" className="h-7 w-auto shrink-0" />
          )}
          {leftTabs.length > 0 ? panelButton('left') : null}
        </div>
        {rightTabs.length > 0 ? panelButton('right') : null}
        <MobileAccountMenu context={context} appCtx={appCtx} />
      </header>

      <div className="relative flex min-h-0 flex-1">
        <main id="main-content" tabIndex={-1} className="relative flex-1 overflow-auto">
          <GlobalLoaderZone>
            {CenterView ? (
              <Suspense fallback={null}>
                <CenterView />
              </Suspense>
            ) : (
              <div className="text-fg-muted flex h-full items-center justify-center px-6 text-center text-sm">
                Pas de vue pour <code className="ml-1">{context}</code>
              </div>
            )}
            <ActiveGlobalLoader />
          </GlobalLoaderZone>
        </main>
        {openSide && sheetTabs.length > 0 ? (
          <div className="bg-bg absolute inset-0 z-10">
            <Panel
              tabs={sheetTabs}
              activeTabId={openSide === 'left' ? leftActiveTab : rightActiveTab}
              onTabClick={openSide === 'left' ? setLeftActiveTab : setRightActiveTab}
              side={openSide}
            />
          </div>
        ) : null}
      </div>

      <MobileTabBar context={context} appCtx={appCtx} />
    </div>
  );
}
